import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAddCampaignContactsMutation, useGetCampaignContactIdsQuery } from "@/store/rest_api";
import LeadPicker from "@/components/LeadPicker";
import { apiErrorMessage } from "@/util/apiError";

interface AddContactsDialogProps {
  campaignId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function AddContactsDialog({ campaignId, open, onOpenChange }: AddContactsDialogProps) {
  const [addContacts, { isLoading: isAdding }] = useAddCampaignContactsMutation();
  const { data: campaignContactIds } = useGetCampaignContactIdsQuery(campaignId, { skip: !open });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const alreadyAddedIds = useMemo(() => new Set(campaignContactIds ?? []), [campaignContactIds]);

  const handleAdd = async () => {
    if (selectedIds.size === 0) {
      return;
    }
    try {
      const added = await addContacts({
        campaignId,
        contactIds: Array.from(selectedIds),
      }).unwrap();
      const addedLabel = added.length === 1 ? "Added 1 lead" : `Added ${added.length} leads`;
      const skippedCount = selectedIds.size - added.length;
      if (skippedCount > 0) {
        toast.warning(`${addedLabel}. ${skippedCount} had no phone number.`);
      } else {
        toast.success(addedLabel);
      }
      setSelectedIds(new Set());
      onOpenChange(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to add leads"));
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedIds(new Set());
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add leads</DialogTitle>
          <DialogDescription>Pick the leads and contacts this campaign should call</DialogDescription>
        </DialogHeader>

        {open && (
          <LeadPicker
            selectedIds={selectedIds}
            onSelectedIdsChange={setSelectedIds}
            alreadyAddedIds={alreadyAddedIds}
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={selectedIds.size === 0 || isAdding}>
            {isAdding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {selectedIds.size > 0 ? `Add ${selectedIds.size}` : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
